<template>
  <f7-page name="invitation">
    <f7-navbar sliding no-shadow back-link :title="invitation.groupName || 'Invitation'">
      <f7-nav-right> </f7-nav-right>
    </f7-navbar>

    <div class="p-4 max-w-lg mx-auto space-y-4">
      <!-- Top Exclusive Tag -->
      <div class="text-align-center">
        <span class="display-inline-flex align-items-center gap-1 px-3 py-1 rounded-full bg-secondary-container/40 text-on-secondary-container font-label-sm font-semibold">
          <f7-icon material="workspace_premium" :size="15" class="text-secondary" />
          Invitation exclusive · Cercle privé
        </span>
      </div>

      <!-- Main Hero Card -->
      <f7-card
        class="invite-hero-card bg-surface-container-lowest rounded-2xl shadow-avocash-sm border border-black/5 relative overflow-hidden"
        :padding="false"
      >
        <div class="absolute -top-10 -right-10 w-32 h-32 bg-primary-fixed/30 rounded-full blur-2xl pointer-events-none"></div>

        <f7-card-content class="invite-hero-content" :padding="false">
          <div class="relative z-10 space-y-3 text-align-center">
            <!-- Avatar + QR -->
            <div class="display-flex flex-direction-column align-items-center justify-content-center gap-2">
              <img v-if="invitation.qr" :src="invitation.qr" :alt="$t('common.qrCode')" class="w-24 h-24 rounded-xl border p-1 bg-white shadow-avocash-sm" />
              <div v-else class="w-16 h-16 rounded-full bg-primary/10 display-flex align-items-center justify-content-center text-primary font-bold text-xl">
                {{ invitation.groupName ? invitation.groupName[0] : 'C' }}
              </div>
              <span class="display-inline-flex align-items-center gap-1 bg-primary text-on-primary px-2.5 py-0.5 rounded-full font-label-sm font-semibold">
                <f7-icon material="verified" :size="12" />
                Cercle Vérifié
              </span>
            </div>

            <h2 class="font-headline-sm text-on-surface font-bold text-xl">{{ invitation.groupName || 'Cercle Communautaire' }}</h2>
            <p class="font-body-sm text-on-surface-variant">
              <span class="font-semibold text-primary">{{ invitation.userName || 'Un membre' }}</span> vous invite à rejoindre le cercle officiel sur <strong class="text-on-surface">Avocash</strong>.
            </p>

            <!-- Quote Note -->
            <div v-if="invitation.description" class="bg-surface-container-low rounded-xl p-3.5 text-align-left border border-black/5">
              <div class="display-flex align-items-flex-start gap-2">
                <f7-icon material="format_quote" :size="18" class="text-primary flex-shrink-0 mt-0.5" />
                <p class="font-body-sm text-on-surface italic leading-relaxed">
                  « {{ invitation.description }} »
                </p>
              </div>
              <div class="text-align-right mt-1 font-label-sm text-on-surface-variant">
                — {{ invitation.userName }}
              </div>
            </div>
          </div>
        </f7-card-content>
      </f7-card>

      <!-- Bento Trust Grid -->
      <div class="grid grid-cols-2 gap-2">
        <f7-card class="trust-tile bg-surface-container-lowest rounded-xl shadow-avocash-sm border border-black/5" :padding="false">
          <f7-card-content class="trust-tile-content space-y-1" :padding="false">
            <div class="display-flex align-items-center justify-content-space-between">
              <f7-icon material="group" :size="20" class="text-primary" />
              <span class="font-label-sm text-primary font-semibold">Actif</span>
            </div>
            <span class="font-headline-sm text-on-surface font-bold text-lg display-block">48</span>
            <span class="font-body-sm text-on-surface-variant">Membres réunis</span>
          </f7-card-content>
        </f7-card>

        <f7-card class="trust-tile bg-surface-container-lowest rounded-xl shadow-avocash-sm border border-black/5" :padding="false">
          <f7-card-content class="trust-tile-content space-y-1" :padding="false">
            <div class="display-flex align-items-center justify-content-space-between">
              <span class="material-symbols-outlined text-secondary" style="font-size: 20px">shield_with_heart</span>
              <span class="font-label-sm text-secondary font-semibold">Niveau Or</span>
            </div>
            <span class="font-headline-sm text-on-surface font-bold text-lg display-block">95%</span>
            <span class="font-body-sm text-on-surface-variant">Indice de confiance</span>
          </f7-card-content>
        </f7-card>
      </div>

      <!-- Perks Card -->
      <f7-card class="perks-card bg-surface-container-lowest rounded-2xl shadow-avocash-sm border border-black/5" :padding="false">
        <f7-card-content class="perks-card-content space-y-3" :padding="false">
          <h3 class="font-label-lg text-on-surface font-bold text-sm display-flex align-items-center gap-1.5">
            <f7-icon material="verified_user" :size="18" class="text-primary" />
            Vos privilèges de membre Avocash
          </h3>

          <div class="space-y-2.5 font-body-sm">
            <div class="display-flex align-items-center gap-2.5">
              <div class="w-7 h-7 rounded-full bg-primary/10 text-primary display-flex align-items-center justify-content-center flex-shrink-0">
                <f7-icon material="payments" :size="16" />
              </div>
              <span>Cotisations 0% sans frais supplémentaires.</span>
            </div>
            <div class="display-flex align-items-center gap-2.5">
              <div class="w-7 h-7 rounded-full bg-primary/10 text-primary display-flex align-items-center justify-content-center flex-shrink-0">
                <f7-icon material="history_edu" :size="16" />
              </div>
              <span>Double signature & gouvernance transparente.</span>
            </div>
          </div>
        </f7-card-content>
      </f7-card>

      <!-- Primary Action CTA -->
      <f7-button
        fill
        large
        raised
        class="width-100 rounded-xl font-headline-sm font-semibold gap-2 shadow-avocash-md"
        @click="updateInvitation(invitation.id, 'ACCEPTED')"
      >
        <span>Accepter l'invitation & Rejoindre</span>
        <f7-icon material="arrow_forward" :size="20" />
      </f7-button>

      <!-- Manage List for Admin -->
      <f7-card
        v-if="isAdmin && invitation.invited?.length"
        class="invited-card bg-surface-container-lowest rounded-2xl shadow-avocash-sm border border-black/5"
        :padding="false"
      >
        <f7-card-content class="invited-card-content space-y-2" :padding="false">
          <h4 class="font-headline-sm text-on-surface font-bold text-sm">{{ $t('common.invitations') }}</h4>
          <div v-for="invit in invitation.invited" :key="invit" class="display-flex justify-content-space-between align-items-center py-1 border-b border-black/5">
            <span class="text-sm">{{ invit }}</span>
            <f7-badge color="red">Envoyé</f7-badge>
          </div>
        </f7-card-content>
      </f7-card>
    </div>
  </f7-page>

  <AdaptiveSheet
    class="updateInvitationsPopup"
    style="--f7-sheet-bg-color: #fff"
    :tablet-fullscreen="true"
    full-screen
    :backdrop="false"
  >
    <f7-view>
      <f7-page
        name="updateInvitation"
        hide-navbar-on-scroll
        ptr
        :ptr-mousewheel="true"
        @ptr:refresh="loadMore"
      >
        <f7-navbar sliding no-shadow>
          <f7-nav-title>{{ $t("common.updateInvitation") }}</f7-nav-title>
          <f7-nav-right>
            <f7-link
              sheet-close=".updateInvitation"
              popup-close=".updateInvitation"
            >
              <f7-icon
                ios="f7:multiply"
                aurora="f7:multiply"
                md="material:close"
              ></f7-icon>
            </f7-link>
          </f7-nav-right>
        </f7-navbar>
        <div class="layout_content">
          <div class="left_layout"></div>
          <div class="main_layout">
            <f7-block-title
              block-title-medium
              v-if="isAdmin"
              class="no-margin-bottom"
              >{{ $t("common.invitations") }}</f7-block-title
            >
            <f7-list v-if="isAdmin">
              <f7-list-group>
                <f7-list-input
                  :label="$t('common.recipients')"
                  :value="recipients"
                  @blur="textChange()"
                  @input="(e) => handleRecipientsInput(e.target.value)"
                  resizable
                  required
                  validate
                  type="textarea"
                  :placeholder="$t('common.clickToAddUser')"
                  :info="$t('common.separateWithComma')"
                  :error-message="$t('common.separateWithComma')"
                >
                  <template #label v-if="invited.length > 0">
                    <f7-block class="invitationRecipients">
                      <f7-chip
                        v-for="(item, index) in invited"
                        :key="index"
                        :class="index > 0 ? 'margin-horizontal-half' : ''"
                        :text="item"
                        deleteable
                        @delete="deleteInvitedChip(item)"
                      ></f7-chip
                    ></f7-block> </template
                ></f7-list-input>
                <f7-block-title block-title-medium class="item-label"
                  >{{ $t("common.description") }}:</f7-block-title
                >
                <f7-text-editor
                  :placeholder="$t('common.description')"
                  :value="description"
                  @texteditor:change="(v) => (description = v)"
                  resizable
                  required
                  validate
                  type="texteditor"
                  :mode="'toolbar'"
                  :style="
                    isNativePlatform || !isTablet
                      ? '--f7-text-editor-height: 300px'
                      : ''
                  "
                ></f7-text-editor>
                <f7-list-input
                  type="datepicker"
                  :value="endDate"
                  :params="{ locale: $i18n.locale, closeOnSelect: true }"
                  :placeholder="$t('common.endDate')"
                  readonly
                  :info="$t('common.selectEndDate')"
                />
                <f7-list simple-list>
                  <span class="item-title" v-html="typeText"></span>
                  <f7-toggle
                    v-model:checked="invitationType"
                    color="auto"
                  ></f7-toggle>
                </f7-list>
              </f7-list-group>
              <f7-button
                ripple
                round-ios
                raised
                fill
                large
                class="buttons"
                style="border-radius: 0px"
                color="primary"
                @click="updateInvitation()"
                >{{ $t("common.update") }}</f7-button
              >
            </f7-list>
          </div>
          <div class="right_layout"></div>
        </div>
      </f7-page>
    </f7-view>
  </AdaptiveSheet>
</template>
<style scoped>
.invite-hero-card,
.trust-tile,
.perks-card,
.invited-card {
  margin: 0;
}

.invite-hero-content {
  padding: 20px;
}

.trust-tile-content {
  padding: 14px;
}

.perks-card-content,
.invited-card-content {
  padding: 16px;
}
</style>
<script setup>
import { ref, computed, onMounted } from 'vue'
import i18n from '@/js/i18n.js'
import { f7 } from 'framework7-vue'
import _ from 'lodash'
import { useInvitation } from '../js/composables/useInvitation.js'
import { useSocialShare } from '../js/composables/useSocialShare.js'
import AdaptiveSheet from '../components/common/AdaptiveSheet.vue'

const props = defineProps({
  f7route: Object,
  f7router: Object,
  entityId: String,
  isAdmin: Boolean,
  entity: Object,
  memberList: Object,
  invite: Object,
})

const description = ref('')
const invitationType = ref('oneTime')
const validUntil = ref('')
const endDate = ref('')
const invited = ref([])
const recipients = ref([])
const invitationToUpdate = ref(null)
const currentInvitation = ref(null)

const {
  profile, isDeskTop, isTablet, locale, documentsUrl, document: authDocument, isNativePlatform,
  userDetail, userAccount, users,
  invitationList, invitation,
  destinationCity, country,
  typeText,
  getGroupInvitationList, loadMore, openUpdateInvite,
  handleRecipientsInput, textChange, deleteInvitedChip,
  updateInvitation, acceptInvitation, changeInvitationStatus,
  deleteInvitation, createNewInvitation, goToinvitation,
} = useInvitation({
  f7route: props.f7route,
  f7router: props.f7router,
  entityId: props.entityId,
  entity: props.entity,
  isAdmin: props.isAdmin,
  memberList: props.memberList,
  description,
  invitationType,
  validUntil,
  endDate,
  invited,
  recipients,
  invitationToUpdate,
})

const { socialShare } = useSocialShare()

function shareInvitation(item) {
  const sharedContent = {
    message: item.description,
    subject: item.groupName,
    files: [item.qr, 'https://avocash.com/34x34.png'],
    short: false,
    linkBase: '',
    url: item.url,
  }
  socialShare(sharedContent)
}

function getInvitation(invitationId) {
  // Original had incomplete function call here
}

function transactionStatus(status) {
  const statusMap = {
    PENDING: { status: 'Pending', color: 'orange' },
    ACCEPTED: { status: 'Accepted', color: 'green' },
    REJECTED: { status: 'Rejected', color: 'red' },
  }
  return statusMap[status] || { status, color: 'gray' }
}

onMounted(() => {
  const invitationId = props.f7route?.params?.invitationId
  if (invitation.value !== null) {
    getInvitation(invitationId)
  }
})

const countries = computed(() => {
  return _.groupBy(country.value, 'continentName')
})
</script>
